import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X, ChevronRight, ChevronDown, Droplets, ShoppingCart } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { COMPANY_INFO } from '../data/siteData';
import { useCart } from '../context/CartContext';
import { useServices } from '../context/ServicesContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_pressure-wash-demo-1/artifacts/gvkp3uo2_Original%20Pressure%20Washer%20Guy.jpg';

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const { itemCount } = useCart();
  const { services } = useServices();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const navLinks = [
    { to: '/about', label: 'About Us' },
    { to: '/photos', label: 'Photos' },
    { to: '/reviews', label: 'Reviews' },
    { to: '/pay', label: 'Pay Invoice' },
    { to: '/contact', label: 'Contact' },
    { to: '/#services', label: 'Book Now or Get a Quote', highlight: true },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-header shadow-md' : 'bg-white/80 backdrop-blur-xl'
      } border-b border-sky-100`}
    >
      {/* Top bar */}
      <div className="bg-sky-700 text-white text-sm py-1.5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a href={`tel:${COMPANY_INFO.phone}`} className="flex items-center gap-1.5 hover:text-sky-200 transition-colors" data-testid="header-phone">
              <Phone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{COMPANY_INFO.phone}</span>
            </a>
            <a href={`mailto:${COMPANY_INFO.email}`} className="flex items-center gap-1.5 hover:text-sky-200 transition-colors" data-testid="header-email">
              <Mail className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{COMPANY_INFO.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
            <img src={LOGO_URL} alt="Top Tier Washing" className="w-10 h-10 object-contain rounded-lg" />
            <span className="font-heading text-xl font-bold text-sky-900 tracking-tight">
              {COMPANY_INFO.name}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {/* Home */}
            <Link to="/" data-testid="nav-link-home"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${location.pathname === '/' ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:text-sky-700 hover:bg-sky-50'}`}>
              Home
            </Link>

            {/* Services dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                data-testid="nav-link-services"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1 ${
                  location.pathname.startsWith('/services') ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:text-sky-700 hover:bg-sky-50'
                }`}
              >
                Services <ChevronDown className={`w-3.5 h-3.5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl border border-sky-100 shadow-xl py-2 z-50" data-testid="services-dropdown">
                  {services.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`dropdown-service-${s.slug}`}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                    >
                      <s.icon className="w-4 h-4 text-sky-500" />
                      {s.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Rest of nav links */}
            {navLinks.map((link) => {
              if (link.to === '/#services') {
                return (
                  <div key={link.to} className="flex items-center gap-1 ml-1">
                    <a
                      href="/#services"
                      onClick={(e) => {
                        e.preventDefault();
                        if (location.pathname === '/') {
                          document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
                        } else {
                          window.location.href = '/#services';
                        }
                      }}
                      data-testid="nav-link-book-now"
                      className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 bg-sky-700 text-white hover:bg-sky-600"
                    >
                      {link.label}
                    </a>
                    <Link
                      to="/book"
                      data-testid="nav-cart-icon"
                      className="relative p-2 rounded-full bg-sky-700 text-white hover:bg-sky-600 transition-colors"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {itemCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-amber-400 text-sky-900 text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">{itemCount}</span>
                      )}
                    </Link>
                  </div>
                );
              }
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    location.pathname === link.to
                      ? 'bg-sky-100 text-sky-700'
                      : 'text-slate-600 hover:text-sky-700 hover:bg-sky-50'
                  } ${link.to === '/contact' ? 'border border-sky-200 ml-1' : ''}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 rounded-lg hover:bg-sky-50 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-sky-100 shadow-lg" data-testid="mobile-nav">
          <div className="px-4 py-3 space-y-1">
            <Link to="/" className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${location.pathname === '/' ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-sky-50 hover:text-sky-700'}`}>
              Home
            </Link>

            {/* Mobile Services expandable */}
            <button
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-between ${
                location.pathname.startsWith('/services') ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-sky-50 hover:text-sky-700'
              }`}
            >
              Services <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileServicesOpen && (
              <div className="pl-4 space-y-0.5">
                {services.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-slate-500 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                  >
                    <s.icon className="w-3.5 h-3.5 text-sky-400" />
                    {s.name}
                  </Link>
                ))}
              </div>
            )}

            {navLinks.map((link) => {
              if (link.to === '/#services') {
                return (
                  <a
                    key={link.to}
                    href="/#services"
                    onClick={(e) => {
                      e.preventDefault();
                      setMobileOpen(false);
                      if (location.pathname === '/') {
                        document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        window.location.href = '/#services';
                      }
                    }}
                    className="block px-4 py-2.5 rounded-xl text-sm font-medium bg-sky-700 text-white text-center"
                  >
                    {link.label}
                  </a>
                );
              }
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    location.pathname === link.to
                      ? 'bg-sky-100 text-sky-700'
                      : 'text-slate-600 hover:bg-sky-50 hover:text-sky-700'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { services } = useServices();
  return (
    <footer data-testid="site-footer" className="bg-sky-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <img src={LOGO_URL} alt="Top Tier Washing" className="w-10 h-10 object-contain rounded-lg" />
              <span className="font-heading text-xl font-bold tracking-tight">
                {COMPANY_INFO.name}
              </span>
            </div>
            <p className="text-sky-200 text-sm leading-relaxed max-w-xs">
              {COMPANY_INFO.tagline}. Professional, reliable, and affordable pressure washing for your home and property.
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Our Services</h4>
            <ul className="space-y-2">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="text-sky-200 hover:text-white text-sm transition-colors"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Service Areas</h4>
            <ul className="space-y-1.5 text-sky-200 text-sm">
              {['Naples', 'Marco Island', 'Ave Maria', 'Bonita Springs', 'Estero', 'Immokalee'].map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Contact Us</h4>
            <div className="space-y-3">
              <div
                className="flex items-center gap-2.5 text-sky-200 text-sm"
              >
                <Phone className="w-4 h-4" />
                {COMPANY_INFO.phone}
              </div>
              <a
                href={`mailto:${COMPANY_INFO.email}`}
                data-testid="footer-email"
                className="flex items-center gap-2.5 text-sky-200 hover:text-white transition-colors text-sm"
              >
                <Mail className="w-4 h-4" />
                {COMPANY_INFO.email}
              </a>
              <Link
                to="/contact"
                data-testid="footer-get-quote"
                className="inline-block mt-3 bg-sky-600 text-white font-semibold rounded-full px-6 py-2.5 text-sm hover:bg-sky-500 transition-colors"
              >
                Get a Free Estimate
              </Link>
            </div>
          </div>
        </div>

        <div className="border-t border-sky-800 mt-12 pt-8 text-center text-sky-300 text-xs">
          &copy; {new Date().getFullYear()} {COMPANY_INFO.name}. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-[104px]">{children}</main>
      <Footer />
    </div>
  );
}
